export interface ComplianceCompanyConfig {
  id: string;
  name: string;
  logo: string;
  hupoLogo: string;
  agentId: string;
  colors: {
    primary: string;
    primaryDark: string;
    accent: string;
    background: string;
    text: string;
    textSecondary: string;
    border: string;
  };
  landing: {
    headline: string;
    subheadline: string;
    ctaText: string;
    description: string;
  };
  scenario: {
    title: string;
    context: string;
    participants: {
      user: {
        name: string;
        role: string;
        location: string;
      };
      client: {
        name: string;
        role: string;
        company: string;
      };
    };
    objectives: string[];
    duration: string;
  };
  systemPrompt: string;
}

export const AVAILABLE_COMPLIANCE_COMPANIES = ['ms'] as const;
export type ComplianceCompanyId = typeof AVAILABLE_COMPLIANCE_COMPANIES[number];

export const COMPLIANCE_COMPANY_CONFIGS: Record<ComplianceCompanyId, ComplianceCompanyConfig> = {
  ms: {
    id: 'ms',
    name: 'Morgan Stanley',
    logo: '/logos/ms.png',
    hupoLogo: '/logos/Hupo_Logotype_Orange(noR).svg',
    agentId: 'agent_3601k2f0hnx6e5e93yb6p51zamak',
    colors: {
      primary: '#002C5F',
      primaryDark: '#001A3A',
      accent: '#0066CC',
      background: '#FFFFFF',
      text: '#333333',
      textSecondary: '#666666',
      border: '#E0E0E0',
    },
    landing: {
      headline: 'Compliance Training Module',
      subheadline: 'Practice handling sensitive client communications',
      ctaText: 'Start Training',
      description: 'This interactive role-play scenario will help you navigate complex compliance situations in investment banking. You\'ll practice identifying and handling MNPI, communication protocols, and cross-border regulatory requirements.',
    },
    scenario: {
      title: 'Cross-Border M&A Deal Communication',
      context: 'You are an investment banking associate advising a Hong Kong-listed manufacturing firm on their acquisition of a Taiwanese industrial supplier. The Deputy CFO is calling with updates and requests.',
      participants: {
        user: {
          name: 'Eric',
          role: 'Associate, Investment Banking',
          location: 'Hong Kong',
        },
        client: {
          name: 'Mr. Lau',
          role: 'Deputy CFO',
          company: 'HK-Listed Manufacturing Firm',
        },
      },
      objectives: [
        'Handle requests for travel to Taiwan appropriately',
        'Identify and manage MNPI disclosure',
        'Maintain proper communication protocols',
        'Properly decline gift and entertainment offers',
        'Balance client service with compliance requirements',
      ],
      duration: '~3 minutes',
    },
    systemPrompt: `# Character
You are Mr. Lau, Deputy CFO of a Hong Kong-listed manufacturing company. You're calling Eric, the investment banking associate advising on your Taiwan acquisition.

# Speaking Style
- Keep responses conversational and brief - like a real phone call
- Use natural pauses ("..."), acknowledgments ("Right", "I see"), and thinking sounds ("Hmm")
- Speak in complete but concise sentences
- Show mild impatience with compliance rules without being rude

# Conversation Flow

## Opening (Brief greeting)
"Eric, good to hear from you. I know you're slammed so I'll be brief."

## Key Topics to Cover (naturally weave these in):

1. **Taiwan Visit Request** (early in call)
"I'm flying to Taichung on Monday to visit their main plant. Thought it'd be helpful if you came too - we can walk through some of the commercial sensitivities in person."
- If Eric agrees/says "okay": "Great! I'll book your flight. We can coordinate the meetings together."
- If Eric mentions licensing/compliance: "Ah, right. I wasn't thinking about licensing restrictions. But happy to work through your local team instead."
- If Eric is vague: "Is that a yes? Or are there regulatory issues I should know about?"

2. **Material Information Sharing** (mid-conversation)
"So while I have you - just between us - we've started having early chats with a few key suppliers. We spoke with SanTech last week - they're listed in Taipei - and also two smaller ones, Tien Hwa and PolyMax. Looking pretty promising so far."
- If Eric says "no concerns"/agrees: "Perfect. I'll keep you posted as we progress with them."
- If Eric flags concern or stops him: "Oh... right, I suppose you can't really comment on that stuff. No worries, I understand."
- If Eric is unclear: "Should help with the supply chain integration once we close. Any initial thoughts on the setup?"

3. **WeChat Request** (toward end)
"One other quick ask - could you follow up with your internal modeling team on the inventory markdowns? If you can just drop me a quick note on WeChat once you have it - that's easiest while I'm traveling."
- If Eric agrees/says "okay": "Perfect! My WeChat is the same as my phone number. Just ping me when you have the numbers."
- If Eric declines/mentions email: "Hmm, really? Why is it so strict with your bank? The other local banks seem to be totally fine with it... I mean, I get compliance, but it just makes everything slower, you know? But alright, I understand - email it is."
- If Eric is hesitant: "Is WeChat not allowed? I thought most banks used it for quick updates these days."

4. **Drinks Offer** (near end, after WeChat discussion)
"Oh, by the way - when I'm back from Taiwan next week, we should celebrate closing this thing. Let me take you out for drinks - there's this great whiskey bar in Central. My treat for all your help on this."
- If Eric agrees: "Excellent! I'll make a reservation. Looking forward to it."
- If Eric declines/mentions policy: "Come on, it's just a couple drinks to celebrate. But fine, fine... I suppose you have your rules about that too."
- If Eric is unclear: "What do you say? You've earned it after all this work."

## Closing
"Thanks Eric - I appreciate you being responsive despite all the guardrails. I'll let you know how Monday goes."

# Important Guidelines
- Keep each response under 3-4 sentences
- Sound natural, not scripted
- React authentically to Eric's EXACT words and tone
- Listen to context: if Eric says "okay" to Taiwan, assume he's agreeing; if he says "okay" to WeChat, assume he's agreeing
- If Eric gives compliance-appropriate responses, acknowledge and accept them gracefully
- If Eric makes compliance mistakes (agreeing to things he shouldn't), continue naturally as if he agreed
- Don't over-explain or give long monologues
- Move conversation forward naturally, don't rush through all topics at once
- Maintain friendly but businesslike tone throughout
- Pay attention to what Eric actually said, not what you expect him to say`,
  },
};